import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Truck, MapPin, Clock, Shield, ChevronLeft, ExternalLink, Phone, Mail } from 'lucide-react';

const LOGO = '/images/atob-logistic-logo-transparent.png';
const HERO_IMAGE = '/images/atob-truck-highway.jpg';

export default function AtoBLogistic() {
  return (
    <main data-testid="atob-logistic-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[480px] overflow-hidden">
        <img src={HERO_IMAGE} alt="AtoB Logistic Solutions Fahrzeugflotte" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0F1A2E]/70"></div>
        <div className="relative z-10 container-main h-full flex flex-col justify-end pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/unternehmen" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider mb-6">
              <ChevronLeft size={16} /> Alle Unternehmen
            </Link>
            <img src={LOGO} alt="AtoB Logistic Solutions GmbH" className="h-16 md:h-20 w-auto mb-6 brightness-0 invert" />
            <p className="text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
              Ihr zuverlässiger Partner für professionelle Logistiklösungen – von A bis B.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Über uns */}
      <section className="section-spacing bg-[#0A1220]">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="accent-line mb-6"></div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
                Über AtoB Logistic Solutions
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">
                AtoB Logistic Solutions GmbH ist Ihr zuverlässiger Partner für professionelle Logistiklösungen. 
                Wir bieten maßgeschneiderte Transportdienstleistungen für Unternehmen jeder Größe – national 
                und international. Mit unserer modernen Fahrzeugflotte und erfahrenen Fahrern garantieren wir 
                pünktliche und sichere Lieferungen.
              </p>
              <p className="text-white/70 leading-relaxed mb-8">
                Als Tochterunternehmen der Basic United Holding GmbH verbinden wir die Flexibilität eines 
                mittelständischen Unternehmens mit der Stärke einer etablierten Unternehmensgruppe.
              </p>
              <div className="p-6 bg-[#0F1A2E]/5 border-l-4 border-[#0F1A2E]">
                <p className="text-sm text-white/50 uppercase tracking-wider mb-1">Geschäftsführung</p>
                <p className="font-semibold text-white">Admir Basic</p>
                <p className="text-sm text-white/70">Alleiniger Gesellschafter und Geschäftsführer</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: Truck, label: 'Moderne Flotte', desc: '40t-Sattelzüge & 3,5t-Transporter' },
                  { icon: MapPin, label: 'National & International', desc: 'Fern- und Nahverkehr' },
                  { icon: Clock, label: 'Termintreue', desc: 'Pünktliche Lieferung garantiert' },
                  { icon: Shield, label: 'GPS-Tracking', desc: 'Transparente Sendungsverfolgung' },
                ].map((item, i) => (
                  <div key={i} className="p-6 bg-[#0F1A2E] border border-[#1e3a5f]">
                    <item.icon size={24} className="text-[#F5A623] mb-4" />
                    <h4 className="font-semibold text-white text-sm mb-1">{item.label}</h4>
                    <p className="text-xs text-white/50">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <ServicesSection />

      {/* Fuhrpark */}
      <FleetSection />

      {/* CTA */}
      <section className="bg-[#0F1A2E] py-20">
        <div className="container-main text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Transportbedarf? Wir sind für Sie da.
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
              Kontaktieren Sie uns für ein individuelles Angebot – schnell, unkompliziert und zuverlässig.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="mailto:info@atob-logistics.de" className="inline-flex items-center justify-center gap-2 bg-[#0A1220] text-[#0F1A2E] px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#F5A623] hover:text-white transition-colors">
                <Mail size={16} /> Anfrage senden
              </a>
              <a href="https://www.atob-logistics.de" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 border border-white text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-[#0A1220] hover:text-[#0F1A2E] transition-colors">
                <ExternalLink size={16} /> Website besuchen
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    { title: 'Fernverkehr', desc: 'Nationale und internationale Transporte mit unseren 40t-Sattelzügen. Zuverlässig, termingerecht und sicher – quer durch Europa.', features: ['Komplettladungen (FTL)', 'Teilladungen (LTL)', 'Express- und Sondertransporte'] },
    { title: 'Nahverkehr', desc: 'Regionale Lieferungen mit unseren 3,5t-Fahrzeugen inkl. Ladebordwand. Perfekt für die letzte Meile und städtische Zustellungen.', features: ['Tagestouren im Umkreis', 'Ladebordwand bis 1.500 kg', 'Flexible Zeitfenster'] },
    { title: 'Logistiklösungen', desc: 'Maßgeschneiderte Logistikkonzepte von der Planung bis zur Durchführung. Wir optimieren Ihre gesamte Lieferkette.', features: ['Supply-Chain-Optimierung', 'Lagerhaltung & Distribution', 'Just-in-Time-Lieferung'] },
  ];

  return (
    <section ref={ref} className="section-spacing bg-[#0F1A2E]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="text-center mb-16">
          <div className="flex justify-center mb-6"><div className="accent-line"></div></div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">Unsere Leistungen</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">Komplette Transportlösungen aus einer Hand – von A bis B.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}
              className="bg-[#0A1220] p-8 border border-[#1e3a5f] hover:shadow-xl hover:border-[#0F1A2E]/20 transition-all duration-500">
              <div className="w-10 h-1 bg-[#F5A623] mb-6"></div>
              <h3 className="font-heading text-xl font-semibold text-white mb-4">{s.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">{s.desc}</p>
              <ul className="space-y-2">
                {s.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle size={14} className="text-[#F5A623] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FleetSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const fleet = [
    { type: '40t-Sattelzüge', desc: 'Für Fernverkehr und Großtransporte', specs: ['Euro 6 Norm', 'GPS-Tracking', 'Digitaler Fahrtenschreiber'] },
    { type: '3,5t-Transporter', desc: 'Für Nahverkehr und Kurierfahrten', specs: ['Ladebordwand', 'Navigationssystem', 'Klimaanlage'] },
  ];

  return (
    <section ref={ref} className="section-spacing bg-[#0A1220]">
      <div className="container-main">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }} className="mb-12">
          <div className="accent-line mb-6"></div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">Unser Fuhrpark</h2>
          <p className="text-lg text-white/70 max-w-2xl">Moderne Fahrzeuge für jeden Transportbedarf.</p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8">
          {fleet.map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}
              className="p-8 border border-[#1e3a5f] bg-[#0F1A2E]">
              <Truck size={28} className="text-[#0F1A2E] mb-4" />
              <h3 className="font-heading text-xl font-semibold text-white mb-2">{v.type}</h3>
              <p className="text-sm text-white/70 mb-4">{v.desc}</p>
              <ul className="space-y-2">
                {v.specs.map((s, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                    <CheckCircle size={14} className="text-[#F5A623]" /> {s}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
